<template>
  <div id="groups-list">
    <h2 class="py-1.5 px-4 text-sm text-gray-500 bg-gray-100 border-b border-gray-200">
      {{ $t('groups.description') }}
    </h2>
    <div
      v-if="mode == 'edit' || groups.length == 0"
      class="px-4 py-2 text-xs text-gray-400 bg-white border-y">
      <button
        @click="addNewGroup"
        type="button"
        class="flex items-center justify-start gap-1 p-1 text-gray-400 transition-colors rounded-sm group hover:text-theme-400">
        <span
          class="p-1 text-gray-400 transition-colors bg-gray-100 rounded-sm group-hover:text-theme-400 group-hover:bg-theme-100"
          ><IconAddSmall
        /></span>
        {{ $t('groups.addGroup') }}
      </button>
    </div>
    <div class="">
      <GroupSingle
        v-for="group in groups"
        :key="group.id"
        :group="group"
        :mode="groupModes.get(group.id) || 'view'"
        :selected="selectedGroup"
        @select="onSelectGroup"
        @contextmenu.prevent="onRightClickGroup({ group, event: $event })"
        @update:group="onUpdateGroup" />
    </div>
    <GroupModalCreateEdit
      v-if="isModalGroupCreateEditActive"
      mode="add"
      :group="selectedGroup"
      :category="category"
      @close-modal="isModalGroupCreateEditActive = false"
      @save="onCreateEditGroup" />

    <ContextMenu
      :options="groupContextMenuOptions"
      class="fixed"
      :isDropdownOpen="isGroupContextMenuOpen"
      :style="{ top: groupClickLocation.y + 'px', left: groupClickLocation.x + 'px' }"
      @action="onGroupAction"
      @close="isGroupContextMenuOpen = false" />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { getGroupItemsByGroup } from '@/services/api/groups';
import { useGroupingStore } from '@/stores/grouping';

import IconAddSmall from '@/assets/icons/IconAddSmall.vue';
import GroupSingle from '@/components/grouping/GroupSingle.vue';
import GroupModalCreateEdit from './GroupModalCreateEdit.vue';
import ContextMenu from '../common/menus/ContextMenu.vue';
import type { Group, GroupItem } from '@/types/Groups';
import type { ContextMenuOptions, TreeType } from '@/types/ComponentTypes';
import { RecordMode } from '@/types/Base';
import { deepCopy } from '@/utils/data';

const {
  groups,
  mode,
  modifiedGroups,
  selectedGroup,
  groupItems,
  groupItemNodes,
  stateGroupItemsNodes,
  category,
  groupModes,
  selectedGroupItem,
  treeNodesLimit,
} = storeToRefs(useGroupingStore());

const { resetGroupModesToView, initializeGroup, clearGroupItemTables } = useGroupingStore();

async function onSelectGroup(group: Group) {
  clearGroupItemTables();
  selectedGroupItem.value = undefined;

  selectedGroup.value = deepCopy<Group>(group);
  if (selectedGroup.value?.groupItems && selectedGroup.value.groupItems.length > 0) {
    groupItems.value = deepCopy(selectedGroup.value.groupItems);
  } else {
    if (selectedGroup.value) {
      const res = await getGroupItemsByGroup(selectedGroup.value.id);
      groupItems.value = res.data || [];
    }
  }
  restoreModifiedGroupChanges(selectedGroup.value);
  groupItemNodes.value = createTreeListNodes(groupItems.value);
  stateGroupItemsNodes.value = deepCopy(groupItemNodes.value);

  groupModes.value.set(group.id, 'view');
}

function restoreModifiedGroupChanges(activeGroup: Group) {
  if (modifiedGroups.value.length == 0) return;
  modifiedGroups.value.forEach((modGroup) => {
    if (activeGroup.id == modGroup.id) {
      if (modGroup.groupItems) {
        groupItems.value = modGroup.groupItems;
      }
    }
  });
}

function createTreeListNodes(items: GroupItem[], parentId: string | null = null): TreeType[] {
  const children = items.filter((item) => {
    const itemParentId = item.parentGroupItemId ?? null;
    return itemParentId === parentId;
  });

  return children
    .map((item) => {
      const childNodes = createTreeListNodes(items, item.id);
      return {
        uid: item.id,
        label: item.name,
        expanded: false,
        parentId: item.parentGroupItemId,
        children: childNodes,
        sequence: item.sequence || 1,
        rootCode: item.rootCode,
        initialChildrenCount: childNodes.length,
        visibleChildrenCount: treeNodesLimit.value,
      };
    })
    .sort((a, b) => a.sequence - b.sequence);
}

const isGroupContextMenuOpen = ref(false);
const groupClickLocation = ref({ x: 0, y: 0 });

const groupContextMenuOptions: ContextMenuOptions[] = [
  { name: 'tree.rename', action: 'rename', icon: 'fluent:rename-24-regular' },
  { name: 'tree.deleteItem', action: 'delete', icon: 'tdesign:delete-1', color: 'text-red-500' },
];

function onRightClickGroup({ group, event }: { group: Group; event: MouseEvent }) {
  if (mode.value == 'view') return;
  isGroupContextMenuOpen.value = true;
  groupClickLocation.value = {
    x: event.clientX,
    y: event.clientY,
  };
  selectedGroup.value = deepCopy<Group>(group);
}

function onGroupAction(action: string) {
  if (!selectedGroup.value) return;
  isGroupContextMenuOpen.value = false;
  if (action == 'rename') groupModes.value.set(selectedGroup.value.id, 'edit');
  if (action == 'delete') onDeleteGroup();
}

function onUpdateGroup(updatedGroup: Group) {
  const groupIndex = groups.value.findIndex((g) => g.id === updatedGroup.id);
  if (groupIndex !== -1) {
    groups.value[groupIndex] = deepCopy<Group>(updatedGroup);
  }

  const modifiedIdx = modifiedGroups.value.findIndex((x) => x.id === updatedGroup.id);

  if (updatedGroup.recordMode === RecordMode.Added) {
    if (modifiedIdx !== -1) modifiedGroups.value[modifiedIdx] = deepCopy<Group>(updatedGroup);
  } else {
    if (modifiedIdx === -1) {
      updatedGroup.recordMode = RecordMode.Modified;
      modifiedGroups.value.push(deepCopy<Group>(updatedGroup));
    } else modifiedGroups.value[modifiedIdx] = deepCopy<Group>(updatedGroup);
  }

  resetGroupModesToView();
  selectedGroup.value = deepCopy<Group>(updatedGroup);
}

function onDeleteGroup() {
  if (!selectedGroup.value) return;
  if (selectedGroup.value.recordMode == RecordMode.Added) {
    const modifiedIndex = modifiedGroups.value.findIndex((x) => x.id === selectedGroup.value?.id);
    if (modifiedIndex !== -1) modifiedGroups.value.splice(modifiedIndex, 1);

    const groupIndex = groups.value.findIndex((x) => x.id === selectedGroup.value?.id);
    if (groupIndex !== -1) groups.value.splice(groupIndex, 1);

    selectedGroup.value = initializeGroup();
    selectedGroupItem.value = undefined;
    groupItemNodes.value = [];
  } else {
    selectedGroup.value.recordMode = RecordMode.Deleted;

    const existingGroupIndex = modifiedGroups.value.findIndex(
      (x) => x.id === selectedGroup.value?.id
    );
    if (existingGroupIndex === -1) {
      modifiedGroups.value.push({ ...selectedGroup.value });
    } else {
      modifiedGroups.value[existingGroupIndex] = { ...selectedGroup.value };
    }

    const groupIndex = groups.value.findIndex((x) => x.id === selectedGroup.value?.id);
    if (groupIndex !== -1) groups.value.splice(groupIndex, 1);

    selectedGroup.value = initializeGroup();
    selectedGroupItem.value = undefined;
    groupItemNodes.value = [];
  }
}

const isModalGroupCreateEditActive = ref(false);

function addNewGroup() {
  resetGroupModesToView();
  selectedGroup.value = initializeGroup();
  groupItemNodes.value = [];
  groupItems.value = [];
  isModalGroupCreateEditActive.value = true;
}

function onCreateEditGroup(group: Group) {
  selectedGroup.value = group;
  modifiedGroups.value.push(group);
  groups.value.push(group);
  mode.value = 'edit';
  isModalGroupCreateEditActive.value = false;
}
</script>
